/**
 * Canvas renderer — Veggie-Art im LMAO-Pseudo-3D-Style (docs/11 + docs/12, BATTLE-14).
 * Consumes ONLY CombatResult snapshots + events (CLAUDE.md rule 5) — it never
 * computes combat logic itself. Floats are fine here (interpolation/FX/anim).
 *
 * Aufbau: SpeciesPainter-Registry (species/) zeichnet Koerper; Renderer liefert
 * einheitlich Schatten, Team-Ring, Waffen (Silhouetten-Regel), Gesicht, Sterne,
 * Pose-Transform (anim.ts), Partikel/Projektile (particles.ts) und Board.
 */
import type { CombatResult, SimEvent, UnitMeta, UnitSnapshot } from '../sim/types'
import type { Role } from '../config/balance'
import {
  ABILITY_COLORS, BOARD, KEYWORD_COLORS, SPECIES, SPECIES_FALLBACK, TEAM_COLORS,
} from '../ui/theme'
import { darken, glow, rgba } from './draw3d'
import { AnimDirector } from './anim'
import { ParticleSystem } from './particles'
import { abilityCastVfx } from './abilityVfx'
import { getSpeciesPainter } from './species'
import { drawUnitSprite, preloadSprites, spriteReady, type SpriteAnimRef } from './spriteSheets'
import { arenaBgImg, BG_FLOOR_TOP_FRAC, preloadArena, tileImg, TILE_REF, TILE_TEX } from './arenaAssets'
import type { Emotion, Pose } from './species/types'
import type { SpeciesColors } from '../ui/theme'

export interface GridDims {
  cols: number
  rows: number // total rows (2 * rowsPerSide)
}

/** Rollenfarben (Legenden/Listen — Units selbst tragen Spezies-Farben) */
export const ROLE_COLORS: Record<Role, string> = {
  tank: '#5b8dd9',
  assassin: '#c9506d',
  ranged: '#d9a441',
  support: '#5bbf7a',
  mage: '#9a6dd7',
}

/** Render-seitiges Wissen: diese Waffen schiessen Projektile statt Melee-Lunge */
const RANGED_WEAPONS = new Set(['pistol', 'rifle'])

const NEUTRAL_POSE: Pose = { squashX: 1, squashY: 1, rot: 0, dx: 0, dy: 0, flash: 0, attackSwing: 0, castPulse: 0 }

// ==== 2.5D Tilted-Perspective + Kamera (BATTLE-17) ====
/** Vertikal-Stauchung der Bodenebene — hoeher = steilere Kamera (mehr von oben) */
const TILT = 0.72
/** Tiefen-Skala: ferne Reihe klein, nahe Reihe gross (bei hoeherer Kamera flacher gestaffelt) */
const FAR_SCALE = 0.8
const NEAR_SCALE = 1.06
const ZOOM_MIN = 0.6
const ZOOM_MAX = 1.6
/** Grid-Cache-Supersampling: bleibt bis ZOOM_MAX knackscharf */
const CACHE_SS = ZOOM_MAX

interface FloatingText {
  unitId: number
  text: string
  color: string
  size: number
  bornMs: number
  ttlMs: number
  offsetX: number
  slot: number // vertikaler Stapel-Slot (F1: kein Zahlen-Matsch)
  mergeKey: string // '' = nicht mergebar; sonst Merge fuer gleiche Unit+Tick
  amount: number // numerisch fuer Merge
  suffix: string // '!' bei Crit
}

interface Pulse {
  unitId: number
  bornMs: number
  ttlMs: number
  color: string
  label: string
}

export interface EditUnitView {
  col: number
  row: number
  champ: string // champion key -> Spezies (docs/12)
  role: Role
  tier?: number // Sternstufe (Anzeige)
  weapons?: string[] // weapon keys -> Silhouetten-Regel
}

/** view passed by the editor UI */
export interface EditView {
  unitsA: EditUnitView[]
  unitsB: EditUnitView[]
  hover: { col: number; row: number } | null
  selected: { col: number; row: number } | null
  brushRole: Role | null
  /** fracture-blocked deployment hexes (Riss-Optik) */
  blocked?: Array<{ col: number; row: number }>
  placeHint?: boolean
}

export class Renderer {
  private readonly ctx: CanvasRenderingContext2D
  private dims: GridDims
  private hexSize = 40
  private originX = 0
  private originY = 0
  // Projektions-Tabellen (2.5D): Skala + kumulierte Y pro Reihe
  private rowScaleTab: number[] = []
  private rowYTab: number[] = []
  // Kamera: Welt = CSS-Koordinaten bei zoom=1
  private zoom = 1
  private panX = 0
  private panY = 0

  private gridCache: HTMLCanvasElement | null = null

  // battle state
  private result: CombatResult | null = null
  private metaById = new Map<number, UnitMeta>()
  private deathTick = new Map<number, number>()
  private eventCursor = 0
  private floats: FloatingText[] = []
  private pulses: Pulse[] = []
  private lastSimTime = 0
  private lastFrameMs = 0

  private readonly anim = new AnimDirector()
  private readonly particles = new ParticleSystem()

  constructor(private readonly canvas: HTMLCanvasElement, dims: GridDims) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    this.ctx = ctx
    this.dims = dims
    preloadSprites() // BATTLE-24: Sprite-Sheets im Hintergrund laden
    // BATTLE-25: Arena-Bilder laden; der statische Grid-Cache muss nach onload neu bauen
    preloadArena(() => {
      this.gridCache = null
    })
    this.layout()
  }

  setDims(dims: GridDims): void {
    this.dims = dims
    this.layout()
  }

  private layout(): void {
    const { cols, rows } = this.dims
    const cssW = this.canvas.clientWidth || 640
    const cssH = this.canvas.clientHeight || 620
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = Math.round(cssW * dpr)
    this.canvas.height = Math.round(cssH * dpr)
    // Groesse aus projizierten Bounds: nahe Kante am breitesten, Hoehe = Summe gestauchter Reihen
    const avg = (FAR_SCALE + NEAR_SCALE) / 2
    const sW = cssW / (Math.sqrt(3) * (cols + 1.3) * NEAR_SCALE)
    const sH = cssH / (1.5 * TILT * avg * (rows - 1) + 4.1)
    this.hexSize = Math.min(sW, sH)
    const s = this.hexSize
    // Reihen-Tabellen: fern (row 0) klein -> nah (letzte row) gross
    this.rowScaleTab = []
    this.rowYTab = []
    let y = 0
    for (let row = 0; row < rows; row++) {
      const k = FAR_SCALE + ((NEAR_SCALE - FAR_SCALE) * row) / Math.max(1, rows - 1)
      if (row > 0) y += (1.5 * s * TILT * (this.rowScaleTab[row - 1]! + k)) / 2
      this.rowScaleTab.push(k)
      this.rowYTab.push(y)
    }
    this.originX = cssW / 2
    this.originY = (cssH - y) / 2 + s * 0.6 // oben Luft fuer Units/Sterne der fernen Reihe
    this.gridCache = null // invalidate — rebuilt lazily on next frame
  }

  /** Tiefen-Skala an einer (interpolierten) Welt-Y-Position */
  private scaleAtY(y: number): number {
    const y0 = this.originY + (this.rowYTab[0] ?? 0)
    const y1 = this.originY + (this.rowYTab[this.rowYTab.length - 1] ?? 1)
    const t = Math.max(0, Math.min(1, (y - y0) / Math.max(1, y1 - y0)))
    return FAR_SCALE + (NEAR_SCALE - FAR_SCALE) * t
  }

  // ==== Kamera (BATTLE-17): Mausrad-Zoom Richtung Cursor + Pan ====

  screenToWorld(px: number, py: number): { x: number; y: number } {
    return { x: (px - this.panX) / this.zoom, y: (py - this.panY) / this.zoom }
  }

  zoomAt(px: number, py: number, factor: number): void {
    const z = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, this.zoom * factor))
    if (z === this.zoom) return
    // Fixpunkt: der Weltpunkt unter dem Cursor bleibt unter dem Cursor
    this.panX = px - ((px - this.panX) * z) / this.zoom
    this.panY = py - ((py - this.panY) * z) / this.zoom
    this.zoom = z
    this.clampPan()
  }

  panBy(dx: number, dy: number): void {
    this.panX += dx
    this.panY += dy
    this.clampPan()
  }

  resetCamera(): void {
    this.zoom = 1
    this.panX = 0
    this.panY = 0
  }

  private clampPan(): void {
    // Board-Zentrum darf den Viewport nicht verlassen (lockere Kappe)
    const cssW = this.canvas.clientWidth || 640
    const cssH = this.canvas.clientHeight || 620
    const cx = this.originX * this.zoom + this.panX
    const cy = (this.originY + (this.rowYTab[this.rowYTab.length - 1] ?? 0) / 2) * this.zoom + this.panY
    if (cx < 0) this.panX -= cx
    if (cx > cssW) this.panX -= cx - cssW
    if (cy < 0) this.panY -= cy
    if (cy > cssH) this.panY -= cy - cssH
  }

  /** Kamera-Steuerung: Rad = Zoom auf Cursor, Mitteltaste/Space+Drag = Pan, 0 = Reset */
  attachCameraControls(canvas: HTMLCanvasElement): void {
    let spaceDown = false
    let panning = false
    let lastX = 0
    let lastY = 0
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space') spaceDown = true
      if (e.code === 'Digit0' || e.code === 'Home') this.resetCamera()
    })
    window.addEventListener('keyup', (e) => {
      if (e.code === 'Space') spaceDown = false
    })
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault()
      const rect = canvas.getBoundingClientRect()
      this.zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.12 : 1 / 1.12)
    }, { passive: false })
    canvas.addEventListener('pointerdown', (e) => {
      if (e.button === 1 || (e.button === 0 && spaceDown)) {
        panning = true
        lastX = e.clientX
        lastY = e.clientY
        canvas.setPointerCapture(e.pointerId)
        e.preventDefault()
      }
    })
    canvas.addEventListener('pointermove', (e) => {
      if (!panning) return
      this.panBy(e.clientX - lastX, e.clientY - lastY)
      lastX = e.clientX
      lastY = e.clientY
    })
    const stopPan = (e: PointerEvent): void => {
      if (panning) canvas.releasePointerCapture(e.pointerId)
      panning = false
    }
    canvas.addEventListener('pointerup', stopPan)
    canvas.addEventListener('pointercancel', stopPan)
  }

  /** static background (Trapez-Rahmen + extrudierte Hexes), supersampled Cache — scharf bis ZOOM_MAX */
  private blitGrid(): void {
    const cssW = this.canvas.clientWidth || 640
    const cssH = this.canvas.clientHeight || 620
    if (!this.gridCache) {
      const cache = document.createElement('canvas')
      const dpr = window.devicePixelRatio || 1
      cache.width = Math.max(1, Math.round(cssW * dpr * CACHE_SS))
      cache.height = Math.max(1, Math.round(cssH * dpr * CACHE_SS))
      const cctx = cache.getContext('2d')!
      cctx.setTransform(dpr * CACHE_SS, 0, 0, dpr * CACHE_SS, 0, 0)
      this.drawBoard(cctx)
      this.gridCache = cache
    }
    this.ctx.drawImage(this.gridCache, 0, 0, cssW, cssH)
  }

  /** Frame-Start: Screen-Clear (identity) + Kamera-Transform (zoom/pan) */
  private beginFrame(): void {
    const ctx = this.ctx
    const dpr = window.devicePixelRatio || 1
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.fillStyle = BOARD.bg
    ctx.fillRect(0, 0, this.canvas.clientWidth || 640, this.canvas.clientHeight || 620)
    ctx.setTransform(dpr * this.zoom, 0, 0, dpr * this.zoom, dpr * this.panX, dpr * this.panY)
  }

  /** Frame-Ende: Vignette im Screen-Space (kamerafest, cinematisch) */
  private endFrame(): void {
    const ctx = this.ctx
    const dpr = window.devicePixelRatio || 1
    const cssW = this.canvas.clientWidth || 640
    const cssH = this.canvas.clientHeight || 620
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    // schwaecher + spaeter einsetzend, damit die ferne Board-Haelfte nicht ins Schwarz saeuft
    const v = ctx.createRadialGradient(cssW / 2, cssH / 2, Math.min(cssW, cssH) * 0.5, cssW / 2, cssH / 2, Math.max(cssW, cssH) * 0.82)
    v.addColorStop(0, 'rgba(0,0,0,0)')
    v.addColorStop(1, 'rgba(0,0,0,0.3)')
    ctx.fillStyle = v
    ctx.fillRect(0, 0, cssW, cssH)
  }

  resize(): void {
    this.layout()
  }

  hexCenter(col: number, row: number): { x: number; y: number } {
    const s = this.hexSize
    const k = this.rowScaleTab[row] ?? 1
    // Mittelachse = Fluchtpunkt; odd-r-Offset ueber beide Paritaeten gemittelt (+0.25)
    const axis = (this.dims.cols - 1) / 2 + 0.25
    const x = this.originX + Math.sqrt(3) * s * (col + 0.5 * (row & 1) - axis) * k
    const y = this.originY + (this.rowYTab[row] ?? 0)
    return { x, y }
  }

  /** inverse picking: Screen -> Welt (Kamera) -> naechstes Hex (Ellipsen-Akzeptanz, gestauchte Tiles) */
  hexAt(px: number, py: number): { col: number; row: number } | null {
    const w = this.screenToWorld(px, py)
    let best: { col: number; row: number } | null = null
    let bestD = Infinity
    let bestK = 1
    for (let row = 0; row < this.dims.rows; row++) {
      const k = this.rowScaleTab[row] ?? 1
      for (let col = 0; col < this.dims.cols; col++) {
        const c = this.hexCenter(col, row)
        const d = (c.x - w.x) ** 2 + ((c.y - w.y) / TILT) ** 2
        if (d < bestD) {
          bestD = d
          best = { col, row }
          bestK = k
        }
      }
    }
    if (best && bestD <= (this.hexSize * bestK * 0.95) ** 2) return best
    return null
  }

  /** Hex-Pfad; sy = Vertikal-Stauchung (Bodenebene TILT; 1 = ungestaucht) */
  private hexPath(cx: number, cy: number, s: number, ctx: CanvasRenderingContext2D = this.ctx, sy: number = TILT): void {
    ctx.beginPath()
    for (let i = 0; i < 6; i++) {
      const angle = ((60 * i - 30) * Math.PI) / 180
      const x = cx + s * Math.cos(angle)
      const y = cy + s * Math.sin(angle) * sy
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    }
    ctx.closePath()
  }

  /**
   * Board: Capybara-Arena (Rasen + Stein-Hex-Tiles, BATTLE-25) sobald die Bilder geladen
   * sind; bis dahin prozeduraler 2.5D-Boden (Trapezrahmen + Gras-Hexes, BATTLE-17).
   */
  private drawBoard(ctx: CanvasRenderingContext2D = this.ctx): void {
    const bg = arenaBgImg()
    const tile = tileImg()
    if (bg && tile) {
      this.drawBoardArena(ctx, bg, tile)
      return
    }
    const half = this.dims.rows / 2
    const s = this.hexSize
    const cols = this.dims.cols
    const rows = this.dims.rows
    // Trapez-Rahmen aus projizierten Eckpunkten (fern schmal, nah breit)
    const kFar = this.rowScaleTab[0]!
    const kNear = this.rowScaleTab[rows - 1]!
    const yFar = this.originY + this.rowYTab[0]! - s * TILT * 1.45 * kFar
    const yNear = this.originY + this.rowYTab[rows - 1]! + s * TILT * 1.65 * kNear
    const halfW = (k: number): number => Math.sqrt(3) * s * ((cols - 1) / 2 + 0.25 + 0.95) * k
    const frameGrad = ctx.createLinearGradient(0, yFar, 0, yNear)
    frameGrad.addColorStop(0, darken(BOARD.stone, 16))
    frameGrad.addColorStop(1, darken(BOARD.stone, 4))
    ctx.save()
    ctx.beginPath()
    ctx.moveTo(this.originX - halfW(kFar), yFar)
    ctx.lineTo(this.originX + halfW(kFar), yFar)
    ctx.lineTo(this.originX + halfW(kNear), yNear)
    ctx.lineTo(this.originX - halfW(kNear), yNear)
    ctx.closePath()
    ctx.lineJoin = 'round'
    ctx.lineWidth = s * 0.4
    ctx.strokeStyle = darken(BOARD.stone, 10)
    ctx.stroke() // runde Kanten
    ctx.fillStyle = frameGrad
    ctx.fill()
    // dunkle Tisch-Vorderkante unter der nahen Seite (Sockel = Pseudo-3D-Tiefe)
    ctx.fillStyle = darken(BOARD.stone, 38)
    ctx.beginPath()
    ctx.moveTo(this.originX - halfW(kNear) - s * 0.2, yNear + s * 0.16)
    ctx.lineTo(this.originX + halfW(kNear) + s * 0.2, yNear + s * 0.16)
    ctx.lineTo(this.originX + halfW(kNear) + s * 0.14, yNear + s * 0.62)
    ctx.lineTo(this.originX - halfW(kNear) - s * 0.14, yNear + s * 0.62)
    ctx.closePath()
    ctx.fill()
    // Innenkante (helle Feder)
    ctx.strokeStyle = 'rgba(240,230,210,0.08)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(this.originX - halfW(kFar) + s * 0.15, yFar + s * 0.12)
    ctx.lineTo(this.originX + halfW(kFar) - s * 0.15, yFar + s * 0.12)
    ctx.lineTo(this.originX + halfW(kNear) - s * 0.15, yNear - s * 0.12)
    ctx.lineTo(this.originX - halfW(kNear) + s * 0.15, yNear - s * 0.12)
    ctx.closePath()
    ctx.stroke()
    ctx.restore()
    // Boden-Hexes: DEZENT in den Arena-Boden integriert (TFT-Feedback: keine dicken Bloecke).
    // Flacher, hellerer Boden, duenne Linien; das starke Platzierungs-Grid kommt nur im Editor.
    for (let row = 0; row < rows; row++) {
      const k = this.rowScaleTab[row]!
      const ts = s * k * 0.96
      const depth = s * 0.045 * TILT * k // fast flach (vorher 0.16)
      for (let col = 0; col < cols; col++) {
        const { x, y } = this.hexCenter(col, row)
        const tone = BOARD.hexTones[(col * 31 + row * 17) % BOARD.hexTones.length]!
        // minimale Seitenkante (nur ein Hauch Tiefe)
        this.hexPath(x, y + depth, ts, ctx)
        ctx.fillStyle = darken(tone, 12)
        ctx.fill()
        // hellere Deckflaeche mit sanftem Gradient (Boden heller)
        this.hexPath(x, y, ts, ctx)
        const g = ctx.createLinearGradient(x, y - ts * TILT, x, y + ts * TILT)
        g.addColorStop(0, darken(tone, -18))
        g.addColorStop(1, darken(tone, -6))
        ctx.fillStyle = g
        ctx.fill()
        // sehr subtile Seiten-Tint (Territorium erkennbar, aber ruhig)
        ctx.fillStyle = row < half ? 'rgba(255,93,93,0.03)' : 'rgba(77,163,255,0.03)'
        ctx.fill()
        // duenne, helle Fugen-Linie statt schwarzem Block-Rand (haelt ferne Struktur)
        ctx.strokeStyle = 'rgba(232,220,190,0.1)'
        ctx.lineWidth = 1
        ctx.stroke()
      }
    }
    // Mittellinie (gold, gestrichelt) auf halber Tiefe
    const yMid = (this.hexCenter(0, half - 1).y + this.hexCenter(0, half).y) / 2
    const kMid = (this.rowScaleTab[half - 1]! + this.rowScaleTab[half]!) / 2
    ctx.strokeStyle = BOARD.centerLine
    ctx.setLineDash([7, 7])
    ctx.lineWidth = 2
    ctx.beginPath()
    ctx.moveTo(this.originX - halfW(kMid) + s * 0.4, yMid)
    ctx.lineTo(this.originX + halfW(kMid) - s * 0.4, yMid)
    ctx.stroke()
    ctx.setLineDash([])
  }

  /**
   * Capybara-Arena (BATTLE-25): Arena-Bild als Untergrund (cover, oben bündig — Mauer/
   * Tribüne bleiben als Kulisse sichtbar). Die Hexplatten werden NICHT als Sprite gemalt
   * (das generierte Tile ist flat-top gedreht und wirkte dadurch schwebend, PO-Feedback),
   * sondern als Stein-TEXTUR auf die exakte Grid-Geometrie geclippt: gleiche pointy-top-
   * Drehung + TILT wie das Grid, volle Tesselierung mit dunkler Fuge, flache Extrusion +
   * Kontaktschatten — das Pflaster liegt IM Boden statt darauf. Reihen fern→nah.
   */
  private drawBoardArena(ctx: CanvasRenderingContext2D, bg: HTMLImageElement, tile: HTMLImageElement): void {
    const cssW = this.canvas.clientWidth || 640
    const cssH = this.canvas.clientHeight || 620
    const s = this.hexSize
    const { cols, rows } = this.dims
    const half = rows / 2
    // Arena-Bild: cover über die volle Canvas-Fläche, horizontal zentriert — und vertikal
    // so gelegt, dass die Kulissen-Bodenlinie (BG_FLOOR_TOP_FRAC) über der Oberkante der
    // hintersten Tile-Reihe bleibt (Tiles dürfen nie in die Mauer ragen).
    const kFarRow = this.rowScaleTab[0] ?? 1
    const yFarTop = this.originY + (this.rowYTab[0] ?? 0) - s * kFarRow * 0.97 * TILT - s * 0.35
    const bgW = bg.naturalWidth
    const bgH = bg.naturalHeight
    const sc = Math.max(cssW / bgW, cssH / bgH, (cssH - yFarTop) / (bgH * (1 - BG_FLOOR_TOP_FRAC)))
    const bgY = Math.min(0, yFarTop - bgH * sc * BG_FLOOR_TOP_FRAC)
    ctx.drawImage(bg, (cssW - bgW * sc) / 2, bgY, bgW * sc, bgH * sc)
    // Textur-Quellfenster: Höhe fix, Breite aus dem Seitenverhältnis des gestauchten Hexes
    // (Breite √3·ts, Höhe 2·ts·TILT) — Textur bleibt unverzerrt, Skala variiert mit der Tiefe
    const fx = tile.naturalWidth / TILE_REF.w
    const fy = tile.naturalHeight / TILE_REF.h
    const texH = TILE_TEX.h
    const texW = texH * (Math.sqrt(3) / (2 * TILT))
    for (let row = 0; row < rows; row++) {
      const k = this.rowScaleTab[row]!
      const ts = s * k * 0.97 // fast volle Zelle — nur eine schmale Fuge bleibt
      const depth = s * 0.05 * TILT * k // flach eingelassen, nicht aufgesetzt
      for (let col = 0; col < cols; col++) {
        const { x, y } = this.hexCenter(col, row)
        // Kontaktschatten unter der Plattenkante (erdet die Platte)
        this.hexPath(x, y + depth * 2.2, ts * 1.02, ctx)
        ctx.fillStyle = 'rgba(50,35,18,0.28)'
        ctx.fill()
        // Seitenkante (Extrusion) in dunklem Sandstein
        this.hexPath(x, y + depth, ts, ctx)
        ctx.fillStyle = '#7c6a48'
        ctx.fill()
        // Deckfläche: Stein-Textur aus dem generierten Tile, geclippt auf UNSER Hex
        // (deterministischer Quell-Versatz pro Hex gegen sichtbare Wiederholung)
        const jx = (((col * 31 + row * 17) % 5) - 2) * (TILE_TEX.jitterX / 2)
        const jy = (((col * 13 + row * 7) % 3) - 1) * TILE_TEX.jitterY
        ctx.save()
        this.hexPath(x, y, ts, ctx)
        ctx.clip()
        const bw = Math.sqrt(3) * ts
        const bh = 2 * ts * TILT
        ctx.drawImage(tile, (TILE_TEX.x + jx) * fx, (TILE_TEX.y + jy) * fy, texW * fx, texH * fy, x - bw / 2, y - bh / 2, bw, bh)
        // Licht von oben wie beim prozeduralen Boden — setzt die Platte in die Bodenebene
        this.hexPath(x, y, ts, ctx)
        const g = ctx.createLinearGradient(x, y - ts * TILT, x, y + ts * TILT)
        g.addColorStop(0, 'rgba(255,248,225,0.10)')
        g.addColorStop(1, 'rgba(60,40,20,0.14)')
        ctx.fillStyle = g
        ctx.fill()
        ctx.restore()
        // schmale dunkle Fuge (Mörtel) — hält die ferne Struktur zusammen
        this.hexPath(x, y, ts, ctx)
        ctx.strokeStyle = 'rgba(74,54,32,0.5)'
        ctx.lineWidth = 1.2
        ctx.stroke()
      }
    }
    // Mittellinie (gold, gestrichelt) — bleibt unsere Team-Hälften-Markierung.
    // Auf hellem Rasen kräftiger als auf dem alten dunklen Boden (0.35 wäre unsichtbar).
    const halfW = (k: number): number => Math.sqrt(3) * s * ((cols - 1) / 2 + 0.25 + 0.95) * k
    const yMid = (this.hexCenter(0, half - 1).y + this.hexCenter(0, half).y) / 2
    const kMid = ((this.rowScaleTab[half - 1] ?? 1) + (this.rowScaleTab[half] ?? 1)) / 2
    ctx.strokeStyle = 'rgba(214,160,42,0.6)'
    ctx.setLineDash([7, 7])
    ctx.lineWidth = 2.5
    ctx.beginPath()
    ctx.moveTo(this.originX - halfW(kMid) + s * 0.4, yMid)
    ctx.lineTo(this.originX + halfW(kMid) - s * 0.4, yMid)
    ctx.stroke()
    ctx.setLineDash([])
  }

  /** Fracture: Riss-Optik mit Glut (docs/11) — Reihen-Skala + Boden-Stauchung */
  private drawFracture(col: number, row: number): void {
    const ctx = this.ctx
    const { x, y } = this.hexCenter(col, row)
    const s = this.hexSize * (this.rowScaleTab[row] ?? 1)
    ctx.save()
    ctx.translate(x, y)
    ctx.scale(1, TILT)
    ctx.translate(-x, -y)
    this.hexPath(x, y, s * 0.94, ctx, 1)
    ctx.fillStyle = BOARD.fractureFill
    ctx.fill()
    ctx.strokeStyle = 'rgba(0,0,0,0.6)'
    ctx.lineWidth = 1.5
    ctx.stroke()
    const seed = col * 7 + row * 13
    const j = (n: number): number => (((seed * 31 + n * 17) % 7) - 3) / 10
    ctx.strokeStyle = BOARD.fractureCrack
    ctx.lineWidth = Math.max(1.5, s * 0.06)
    ctx.shadowColor = BOARD.fractureCrack
    ctx.shadowBlur = s * 0.2
    ctx.beginPath()
    ctx.moveTo(x - s * 0.45, y - s * 0.3)
    ctx.lineTo(x - s * 0.1 + j(1) * s, y - s * 0.05 + j(2) * s)
    ctx.lineTo(x + s * 0.12 + j(3) * s, y + s * 0.1 + j(4) * s)
    ctx.lineTo(x + s * 0.42, y + s * 0.38)
    ctx.moveTo(x - s * 0.05 + j(5) * s, y + j(6) * s)
    ctx.lineTo(x + s * 0.2, y - s * 0.35)
    ctx.stroke()
    ctx.shadowBlur = 0
    ctx.restore()
  }

  // ============ VEGGIE CHARACTERS (docs/12 §1 + BATTLE-14 Pose/Volumen) ============

  private drawVeggie(
    x: number, y: number, baseR: number,
    opts: {
      champ: string
      team: 'A' | 'B'
      tier: number
      weapons: string[]
      emotion: Emotion
      pose?: Pose
      alpha?: number
      /** BATTLE-24: generiertes Sprite statt prozeduralem Painter (Fallback: Painter) */
      anim?: SpriteAnimRef
    },
  ): void {
    const ctx = this.ctx
    const colors = SPECIES[opts.champ] ?? SPECIES_FALLBACK
    const tier = Math.max(1, opts.tier)
    const r = baseR * (1 + (tier - 1) * 0.11) // Sternstufen-Eskalation (docs/11)
    const dead = opts.emotion === 'dead'
    const pose = opts.pose ?? NEUTRAL_POSE

    // ===== 3/4-GROUNDING (BATTLE-17): Figur STEHT auf dem Hex =====
    // Koerper leicht angehoben, Fuesse + Kontakt-Schatten am Boden, Team-gespiegelte
    // 3/4-Drehung bricht die frontale Sticker-Optik (User-Feedback TFT-Look).
    // Bodenkontakt (Fuesse + Schatten) sitzt AUF der Hex-Mitte, Koerper steigt darueber auf.
    const footY = y + r * 0.08 // Standpunkt = Hex-Tile-Mitte (leicht nach vorne)
    const cy = footY - r * 0.78 // Kopf-/Koerper-Zentrum hebt sich vom Standpunkt ab
    const turn = dead ? 0 : opts.team === 'A' ? 0.42 : -0.42 // 3/4-Drehung (Team-gespiegelt)
    const forward = turn >= 0 ? 1 : -1
    // BATTLE-24: Sprite-Pfad, wenn Sheets vorhanden UND geladen (sonst Painter — Mischbetrieb)
    const useSprite = opts.anim !== undefined && spriteReady(opts.champ)

    ctx.save()
    ctx.globalAlpha = opts.alpha ?? 1

    // --- Boden-Ebene (NICHT mit-posiert): Kontakt-Schatten + flacher Team-Ring ---
    const shX = x + pose.dx * 0.35
    const shY = footY + r * 0.02
    const sg = ctx.createRadialGradient(shX, shY, r * 0.08, shX, shY, r * 0.9)
    sg.addColorStop(0, `rgba(0,0,0,${dead ? 0.24 : 0.42})`)
    sg.addColorStop(0.7, `rgba(0,0,0,${dead ? 0.1 : 0.2})`)
    sg.addColorStop(1, 'rgba(0,0,0,0)')
    ctx.fillStyle = sg
    ctx.beginPath()
    ctx.ellipse(shX, shY, r * 0.82, r * 0.24, 0, 0, Math.PI * 2)
    ctx.fill()
    if (!dead) {
      const teamCol = TEAM_COLORS[opts.team]
      ctx.save()
      ctx.strokeStyle = rgba(teamCol, 0.55)
      ctx.lineWidth = Math.max(2.5, r * 0.12)
      ctx.shadowColor = teamCol
      ctx.shadowBlur = r * 0.3
      ctx.beginPath()
      ctx.ellipse(x, shY, r * 0.64, r * 0.19, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.shadowBlur = 0
      ctx.restore()
      // Fuesse (3/4-Stand) nur fuer prozedurale Painter — Sprites bringen eigene Beine mit
      if (!useSprite) this.drawFeet(x + pose.dx * 0.3, footY, r, colors, forward)
    }

    // --- Charakter (Kopf/Koerper): Pose + spuerbare 3/4-Drehung um Off-Center-Pivot ---
    // Rotation ~6° + Skalierung um einen zur Blickrichtung verschobenen Pivot => die
    // abgewandte Seite verkuerzt staerker (echtes 3/4 statt symmetrischem Duenner-Sticker).
    const pivotX = x + forward * r * 0.14
    ctx.save()
    ctx.translate(pivotX + pose.dx, cy + pose.dy)
    ctx.rotate(pose.rot + turn * (useSprite ? 0.06 : 0.24)) // Sprites sind schon 3/4
    ctx.scale(useSprite ? pose.squashX : pose.squashX * 0.89, pose.squashY)
    ctx.translate(-pivotX, -cy)

    // two-handers hinter dem Koerper (eine dominante Silhouette, docs/12 §3)
    if (!useSprite) this.drawWeapons(x, cy, r, opts.weapons, 'behind', dead, pose.attackSwing)

    // Sternstufen-Grandeur (F3): 3★ Gold-Aura + Gold-Rim, 2★ dezenter Silber-Rim
    if (tier >= 3 && !dead) {
      glow(ctx, x, cy, r * 1.6, KEYWORD_COLORS.holy, 0.45 + pose.castPulse * 0.25)
      ctx.save()
      ctx.strokeStyle = rgba(KEYWORD_COLORS.holy, 0.9)
      ctx.lineWidth = Math.max(2, r * 0.09)
      ctx.shadowColor = KEYWORD_COLORS.holy
      ctx.shadowBlur = r * 0.3
      ctx.beginPath()
      ctx.ellipse(x, cy, r * 1.06, r * 1.02, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    } else if (tier === 2 && !dead) {
      ctx.save()
      ctx.strokeStyle = 'rgba(214, 222, 230, 0.7)'
      ctx.lineWidth = Math.max(1.5, r * 0.06)
      ctx.beginPath()
      ctx.ellipse(x, cy, r * 1.04, r * 1.0, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    }

    if (useSprite) {
      // BATTLE-24: generierter Charakter (enthaelt Koerper, Waffe, Gesicht).
      // Groesse: Figur fuellt ~55% des 400er-Frames -> Frame ~4r hoch zeichnen.
      drawUnitSprite(
        this.ctx, opts.champ, opts.anim!,
        x, footY, r * 4.0,
        opts.team === 'B', // Sprites schauen nativ nach rechts -> B spiegeln
        dead,
      )
    } else {
    // Spezies-Koerper via Registry (LMAO-Sub-Agent-Dateien)
    const deadColors = { body: '#6a6f66', bodyDark: '#53574f', accent: '#5c6157', outline: '#23261f' }
    getSpeciesPainter(opts.champ)({
      ctx, x, y: cy, r,
      colors: dead ? deadColors : colors,
      team: opts.team, tier, emotion: opts.emotion, pose, weapons: opts.weapons,
    })

    // 2★+: Schulterplatten-Akzent (einheitlich)
    if (tier >= 2 && !dead) {
      const g = ctx.createLinearGradient(x, cy - r * 0.3, x, cy + r * 0.1)
      g.addColorStop(0, '#d7dee6')
      g.addColorStop(1, '#8b95a0')
      ctx.fillStyle = g
      ctx.strokeStyle = colors.outline
      ctx.lineWidth = Math.max(1.5, r * 0.08)
      for (const side of [-1, 1]) {
        ctx.beginPath()
        ctx.ellipse(x + side * r * 0.72, cy - r * 0.1, r * 0.28, r * 0.2, side * 0.5, 0, Math.PI * 2)
        ctx.fill()
        ctx.stroke()
      }
    }

    // one-handers / dual wield vor dem Koerper (Schwung via attackSwing)
    this.drawWeapons(x, cy, r, opts.weapons, 'front', dead, pose.attackSwing)

    this.drawFace(ctx, x, cy, r, opts.emotion, turn)
    } // Ende Painter-Zweig (BATTLE-24)

    // Hit-Flash (weisses Aufblitzen ueber der Figur)
    if (pose.flash > 0) {
      ctx.save()
      ctx.globalCompositeOperation = 'lighter'
      ctx.globalAlpha = pose.flash * 0.55
      ctx.fillStyle = '#fff6e0'
      ctx.beginPath()
      ctx.ellipse(x, cy - r * 0.05, r * 0.95, r * 1.05, 0, 0, Math.PI * 2)
      ctx.fill()
      ctx.restore()
    }
    ctx.restore() // Pose/3-4-Transform Ende

    // Sternstufe wird NICHT mehr ueber der Figur gezeichnet (verdeckte die Sprites,
    // PO-Feedback) — sie sitzt jetzt als Badge rechts neben dem Lebensbalken
    // (drawTierBadge; Callsites: Battle-Bars + Editor).
    ctx.restore()
  }

  /** Sternstufen-Badge, links-buendig ab x (rechts neben dem Lebensbalken, PO-Wunsch) */
  private drawTierBadge(x: number, cy: number, tier: number, r: number): void {
    const t = Math.max(1, Math.min(3, tier))
    const stars = '★'.repeat(t)
    const starSize = Math.max(9, r * 0.34)
    const ctx = this.ctx
    ctx.save()
    ctx.font = `bold ${starSize}px system-ui`
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    const w = ctx.measureText(stars).width
    ctx.fillStyle = 'rgba(14, 11, 7, 0.72)'
    ctx.beginPath()
    ctx.roundRect(x - starSize * 0.2, cy - starSize * 0.62, w + starSize * 0.4, starSize * 1.24, starSize * 0.5)
    ctx.fill()
    ctx.lineWidth = 2.5
    ctx.strokeStyle = 'rgba(0,0,0,0.8)'
    ctx.strokeText(stars, x, cy)
    ctx.fillStyle = t >= 3 ? KEYWORD_COLORS.holy : t === 2 ? '#e8c86a' : 'rgba(240,230,210,0.7)'
    ctx.fillText(stars, x, cy)
    ctx.restore()
  }

  /** kleine Fuesse fuer den 3/4-Stand — Bodenkontakt/Verankerung (BATTLE-17) */
  private drawFeet(x: number, footY: number, r: number, colors: SpeciesColors, forward: number): void {
    const ctx = this.ctx
    ctx.save()
    ctx.fillStyle = darken(colors.body, 32)
    ctx.strokeStyle = colors.outline
    ctx.lineWidth = Math.max(1, r * 0.045)
    // Lesbarkeits-Floor: auch in fernen (kleinen r) Reihen bleiben Fuesse sichtbar
    const fw = Math.max(3, r * 0.26)
    const fh = Math.max(2, r * 0.14)
    // hinterer Fuss (kleiner, seitlich gespreizt — schaut unter der Body-Silhouette hervor)
    ctx.beginPath()
    ctx.ellipse(x - forward * r * 0.34, footY - r * 0.04, fw * 0.85, fh * 0.85, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    // vorderer Fuss (groesser, in Blickrichtung vorne)
    ctx.beginPath()
    ctx.ellipse(x + forward * r * 0.32, footY + r * 0.06, fw, fh, 0, 0, Math.PI * 2)
    ctx.fill()
    ctx.stroke()
    ctx.restore()
  }

  /** grosse Augen + Emotions-States (docs/11 Expressions & Personality) */
  private drawFace(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, emotion: Emotion, turn = 0): void {
    // 3/4-Blick: Features in Drehrichtung verschoben, vorderes Auge groesser + weiter aussen,
    // fernes Auge kleiner + naeher zur Mitte (Foreshortening = staerkster 3/4-Cue).
    const fs = turn * r * 0.24
    const frontSide = turn >= 0 ? 1 : -1
    const eyeSizeFac = (side: number): number => (side === frontSide ? 1.12 : 0.8)
    const eyeDxOf = (side: number): number => (side === frontSide ? r * 0.32 : r * 0.22)
    const eyeY = y - r * 0.18
    const eyeR = r * 0.24
    ctx.save()
    ctx.lineWidth = Math.max(1.5, r * 0.08)
    if (emotion === 'dead') {
      ctx.strokeStyle = '#2b2e28'
      for (const side of [-1, 1]) {
        const ex = x + side * eyeDxOf(side) + fs
        ctx.beginPath()
        ctx.moveTo(ex - eyeR * 0.6, eyeY - eyeR * 0.6)
        ctx.lineTo(ex + eyeR * 0.6, eyeY + eyeR * 0.6)
        ctx.moveTo(ex + eyeR * 0.6, eyeY - eyeR * 0.6)
        ctx.lineTo(ex - eyeR * 0.6, eyeY + eyeR * 0.6)
        ctx.stroke()
      }
      ctx.beginPath()
      ctx.moveTo(x - r * 0.25 + fs, y + r * 0.32)
      ctx.lineTo(x + r * 0.25 + fs, y + r * 0.32)
      ctx.stroke()
      ctx.restore()
      return
    }
    // whites (mit minimaler Woelbung: Gradient)
    for (const side of [-1, 1]) {
      const ex = x + side * eyeDxOf(side) + fs
      const er = eyeR * eyeSizeFac(side)
      const g = ctx.createRadialGradient(ex - er * 0.3, eyeY - er * 0.35, er * 0.2, ex, eyeY, er * 1.2)
      g.addColorStop(0, '#ffffff')
      g.addColorStop(1, '#ded8c8')
      ctx.beginPath()
      ctx.ellipse(ex, eyeY, er, er * 1.15, 0, 0, Math.PI * 2)
      ctx.fillStyle = g
      ctx.fill()
      ctx.strokeStyle = 'rgba(0,0,0,0.55)'
      ctx.stroke()
    }
    // pupils + Glanzpunkt
    const pupilBase = emotion === 'surprised' ? eyeR * 0.62 : eyeR * 0.45
    for (const side of [-1, 1]) {
      const ex = x + side * eyeDxOf(side) + fs
      const pupilR = pupilBase * eyeSizeFac(side)
      // Pupille leicht in Blickrichtung (verstaerkt 3/4)
      ctx.fillStyle = '#1d1a14'
      ctx.beginPath()
      ctx.arc(ex + fs * 0.4, eyeY + eyeR * 0.1, pupilR, 0, Math.PI * 2)
      ctx.fill()
      ctx.fillStyle = 'rgba(255,255,255,0.85)'
      ctx.beginPath()
      ctx.arc(ex + fs * 0.4 - pupilR * 0.3, eyeY - pupilR * 0.15, pupilR * 0.28, 0, Math.PI * 2)
      ctx.fill()
    }
    // brows
    ctx.strokeStyle = '#241f16'
    ctx.lineWidth = Math.max(2, r * 0.1)
    for (const side of [-1, 1]) {
      const ex = x + side * eyeDxOf(side) + fs
      ctx.beginPath()
      if (emotion === 'angry') {
        ctx.moveTo(ex - side * eyeR * 0.9, eyeY - eyeR * 1.5)
        ctx.lineTo(ex + side * eyeR * 0.7, eyeY - eyeR * 0.85)
      } else if (emotion === 'worried') {
        ctx.moveTo(ex - side * eyeR * 0.8, eyeY - eyeR * 1.0)
        ctx.lineTo(ex + side * eyeR * 0.7, eyeY - eyeR * 1.55)
      } else {
        ctx.moveTo(ex - eyeR * 0.75, eyeY - eyeR * 1.35)
        ctx.lineTo(ex + eyeR * 0.75, eyeY - eyeR * 1.35)
      }
      ctx.stroke()
    }
    // mouth
    ctx.beginPath()
    const mouthY = y + r * 0.3
    const mx = x + fs
    if (emotion === 'angry') {
      ctx.moveTo(mx - r * 0.22, mouthY + r * 0.06)
      ctx.quadraticCurveTo(mx, mouthY - r * 0.12, mx + r * 0.22, mouthY + r * 0.06)
    } else if (emotion === 'surprised') {
      ctx.ellipse(mx, mouthY, r * 0.14, r * 0.18, 0, 0, Math.PI * 2)
    } else if (emotion === 'worried') {
      ctx.moveTo(mx - r * 0.2, mouthY)
      ctx.quadraticCurveTo(mx - r * 0.05, mouthY + r * 0.08, mx + r * 0.08, mouthY - r * 0.02)
      ctx.quadraticCurveTo(mx + r * 0.16, mouthY - r * 0.08, mx + r * 0.22, mouthY + r * 0.02)
    } else {
      ctx.moveTo(mx - r * 0.18, mouthY - r * 0.02)
      ctx.quadraticCurveTo(mx, mouthY + r * 0.12, mx + r * 0.18, mouthY - r * 0.02)
    }
    ctx.stroke()
    ctx.restore()
  }

  /**
   * Waffen nach Silhouetten-Regel (docs/12 §3). swing (0..1) rotiert die Waffen
   * fuer den Angriffs-Schwung (Anticipation->Chop, docs/11 Animation Style).
   */
  private drawWeapons(
    x: number, y: number, r: number, weapons: string[],
    layer: 'behind' | 'front', dead: boolean, swing = 0,
  ): void {
    if (dead || weapons.length === 0) return
    const ctx = this.ctx
    const swingRot = Math.sin(swing * Math.PI) * 0.85
    const twoHander = weapons.find((w) => w === 'greataxe' || w === 'rifle')
    if (twoHander && layer === 'behind') {
      ctx.save()
      ctx.lineWidth = Math.max(1.5, r * 0.07)
      ctx.strokeStyle = '#241f16'
      if (twoHander === 'greataxe') {
        // F2: dominante, ueberdimensionierte Silhouette (docs/11 "iconic")
        ctx.save()
        ctx.translate(x + r * 0.9, y + r * 0.05)
        ctx.rotate(-0.35 + swingRot)
        const poleGrad = ctx.createLinearGradient(-r * 0.12, 0, r * 0.12, 0)
        poleGrad.addColorStop(0, '#a5794a')
        poleGrad.addColorStop(1, '#6e4c28')
        ctx.fillStyle = poleGrad
        ctx.fillRect(-r * 0.12, -r * 1.85, r * 0.24, r * 2.95)
        ctx.strokeRect(-r * 0.12, -r * 1.85, r * 0.24, r * 2.95)
        const bladeGrad = ctx.createLinearGradient(0, -r * 1.8, r * 1.5, -r * 0.5)
        bladeGrad.addColorStop(0, '#eef2f6')
        bladeGrad.addColorStop(0.6, '#c3ccd6')
        bladeGrad.addColorStop(1, '#8c96a2')
        ctx.fillStyle = bladeGrad
        ctx.beginPath()
        ctx.moveTo(0, -r * 1.82)
        ctx.quadraticCurveTo(r * 1.55, -r * 1.35, r * 0.42, -r * 0.4)
        ctx.quadraticCurveTo(r * 0.5, -r * 1.15, 0, -r * 1.15)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.restore()
      } else {
        ctx.save()
        ctx.translate(x - r * 0.1, y + r * 0.4)
        ctx.rotate(-0.28 + swingRot * 0.15)
        const stockGrad = ctx.createLinearGradient(0, -r * 0.22, 0, r * 0.24)
        stockGrad.addColorStop(0, '#7a5a33')
        stockGrad.addColorStop(1, '#4a3620')
        ctx.fillStyle = stockGrad
        ctx.fillRect(-r * 1.5, -r * 0.22, r * 1.15, r * 0.46)
        ctx.strokeRect(-r * 1.5, -r * 0.22, r * 1.15, r * 0.46)
        const barrelGrad = ctx.createLinearGradient(0, -r * 0.13, 0, r * 0.13)
        barrelGrad.addColorStop(0, '#c3ccd6')
        barrelGrad.addColorStop(1, '#77828d')
        ctx.fillStyle = barrelGrad
        ctx.fillRect(-r * 0.4, -r * 0.13, r * 2.3, r * 0.26)
        ctx.strokeRect(-r * 0.4, -r * 0.13, r * 2.3, r * 0.26)
        ctx.fillRect(r * 1.8, -r * 0.2, r * 0.28, r * 0.4)
        ctx.restore()
      }
      ctx.restore()
      return
    }
    if (twoHander) return
    if (layer !== 'front') return
    const oneHanders = weapons.filter((w) => w !== 'fist')
    if (oneHanders.length === 0) return
    const dual = oneHanders.length >= 2
    const size = dual ? r * 0.72 : r * 0.98 // F2: groessere Klingen
    const sides = dual ? [-1, 1] : [1]
    ctx.save()
    ctx.lineWidth = Math.max(1.5, r * 0.07)
    ctx.strokeStyle = '#241f16'
    sides.forEach((side, i) => {
      const key = oneHanders[Math.min(i, oneHanders.length - 1)]!
      const wx = x + side * r * 0.88
      const wy = y + r * 0.15
      ctx.save()
      ctx.translate(wx, wy)
      ctx.rotate(side * (0.35 + swingRot))
      if (key === 'pistol') {
        const g = ctx.createLinearGradient(-size * 0.12, 0, size * 0.12, 0)
        g.addColorStop(0, '#c3ccd6')
        g.addColorStop(1, '#77828d')
        ctx.fillStyle = g
        ctx.fillRect(-size * 0.12, -size * 0.75, size * 0.24, size * 0.8)
        ctx.strokeRect(-size * 0.12, -size * 0.75, size * 0.24, size * 0.8)
        ctx.fillStyle = '#5e4526'
        ctx.fillRect(-size * 0.12, 0, size * 0.3, size * 0.3)
        ctx.strokeRect(-size * 0.12, 0, size * 0.3, size * 0.3)
      } else {
        const bladeGrad = ctx.createLinearGradient(-size * 0.2, -size, size * 0.2, 0)
        if (key === 'poisondagger') {
          bladeGrad.addColorStop(0, '#c4f09a')
          bladeGrad.addColorStop(1, '#6da844')
        } else {
          bladeGrad.addColorStop(0, '#f0f4f8')
          bladeGrad.addColorStop(1, '#98a2ae')
        }
        ctx.fillStyle = bladeGrad
        ctx.beginPath()
        ctx.moveTo(0, -size)
        ctx.lineTo(size * 0.2, -size * 0.15)
        ctx.lineTo(-size * 0.2, -size * 0.15)
        ctx.closePath()
        ctx.fill()
        ctx.stroke()
        ctx.fillStyle = '#b8963f'
        ctx.fillRect(-size * 0.26, -size * 0.15, size * 0.52, size * 0.1)
        ctx.fillStyle = '#5e4526'
        ctx.fillRect(-size * 0.07, -size * 0.05, size * 0.14, size * 0.32)
        ctx.strokeRect(-size * 0.07, -size * 0.05, size * 0.14, size * 0.32)
      }
      ctx.restore()
    })
    ctx.restore()
  }

  // ============ EDIT MODE ============

  drawEdit(view: EditView): void {
    this.beginFrame()
    this.blitGrid()
    const ctx = this.ctx
    // BATTLE-25: auf hellem Steinboden brauchen Grid/Hover dunkle bzw. goldene Kontraste
    const onStone = arenaBgImg() !== null && tileImg() !== null
    // Platzierungs-Grid: NUR im Editor deutlich sichtbar (im Kampf bleibt der Boden ruhig).
    ctx.save()
    ctx.strokeStyle = onStone ? 'rgba(46,50,32,0.38)' : 'rgba(232,220,190,0.18)'
    ctx.lineWidth = 1.2
    for (let row = 0; row < this.dims.rows; row++) {
      const k = this.rowScaleTab[row] ?? 1
      for (let col = 0; col < this.dims.cols; col++) {
        const { x, y } = this.hexCenter(col, row)
        this.hexPath(x, y, this.hexSize * k * 0.9)
        ctx.stroke()
      }
    }
    ctx.restore()
    for (const b of view.blocked ?? []) {
      this.drawFracture(b.col, b.row)
    }
    if (view.hover) {
      const { x, y } = this.hexCenter(view.hover.col, view.hover.row)
      this.hexPath(x, y, this.hexSize * (this.rowScaleTab[view.hover.row] ?? 1) * 0.94)
      ctx.fillStyle = onStone ? 'rgba(255,215,94,0.25)' : 'rgba(240,230,210,0.12)'
      ctx.fill()
    }
    if (view.selected) {
      const { x, y } = this.hexCenter(view.selected.col, view.selected.row)
      this.hexPath(x, y, this.hexSize * (this.rowScaleTab[view.selected.row] ?? 1) * 0.98)
      ctx.strokeStyle = KEYWORD_COLORS.holy
      ctx.lineWidth = 2.5
      ctx.stroke()
    }
    // fern -> nah zeichnen (Y-Sorting)
    const all: Array<{ team: 'A' | 'B'; u: EditUnitView }> = [
      ...view.unitsA.map((u) => ({ team: 'A' as const, u })),
      ...view.unitsB.map((u) => ({ team: 'B' as const, u })),
    ].sort((a, b) => a.u.row - b.u.row)
    for (const { team, u } of all) {
      const { x, y } = this.hexCenter(u.col, u.row)
      const ur = this.hexSize * 0.58 * (this.rowScaleTab[u.row] ?? 1)
      this.drawVeggie(x, y, ur, {
        champ: u.champ, team, tier: u.tier ?? 1, weapons: u.weapons ?? [], emotion: 'neutral',
        anim: { kind: 'idle', timeMs: performance.now() },
      })
      // Sternstufe an derselben Stelle wie im Kampf: rechts neben der (gedachten) Balken-Zeile
      const headY = spriteReady(u.champ) ? ur * 2.75 : ur * 1.48
      this.drawTierBadge(x + ur * 1.05 + 4, y - headY + 4, u.tier ?? 1, ur)
    }
    this.endFrame()
  }

  // ============ BATTLE MODE ============

  prepare(result: CombatResult): void {
    this.result = result
    this.metaById = new Map(result.units.map((u) => [u.id, u]))
    this.deathTick = new Map()
    for (const e of result.events) {
      if (e.type === 'death') this.deathTick.set(e.unit, e.tick)
    }
    this.resetPlaybackFx()
  }

  resetPlaybackFx(): void {
    this.eventCursor = 0
    this.floats = []
    this.pulses = []
    this.lastSimTime = 0
    this.anim.reset()
    this.particles.reset()
  }

  /** interpolated render position of a unit at simTime */
  private unitPos(snapA: UnitSnapshot, snapB: UnitSnapshot | undefined, alpha: number): { x: number; y: number } {
    const posOf = (s: UnitSnapshot): { x: number; y: number } => {
      const from = this.hexCenter(s.fromCol, s.fromRow)
      const to = this.hexCenter(s.col, s.row)
      const p = s.movePct / 100
      return { x: from.x + (to.x - from.x) * p, y: from.y + (to.y - from.y) * p }
    }
    const a = posOf(snapA)
    if (!snapB) return a
    const b = posOf(snapB)
    return { x: a.x + (b.x - a.x) * alpha, y: a.y + (b.y - a.y) * alpha }
  }

  /** consume events in (lastSimTime, simTime] and spawn FX/Anim */
  private processEvents(
    simTime: number, nowMs: number,
    posFn: (id: number) => { x: number; y: number },
    snapFn: (id: number) => UnitSnapshot | undefined,
  ): void {
    if (!this.result) return
    if (simTime < this.lastSimTime) {
      this.eventCursor = 0
      this.floats = []
      this.pulses = []
      this.anim.reset()
      this.particles.reset()
      while (this.eventCursor < this.result.events.length && this.result.events[this.eventCursor]!.tick <= simTime) {
        this.eventCursor++
      }
      this.lastSimTime = simTime
      return
    }
    const events = this.result.events
    while (this.eventCursor < events.length && events[this.eventCursor]!.tick <= simTime) {
      const e = events[this.eventCursor]!
      if (e.tick > this.lastSimTime) this.spawnFx(e, nowMs, posFn, snapFn)
      this.eventCursor++
    }
    this.lastSimTime = simTime
  }

  /**
   * F1: Damage/Heal-Zahl setzen — mergt gleiche Unit+mergeKey im selben Tick-Batch
   * (nowMs), sonst neuer Slot oben auf dem Stapel. Verhindert ueberlappenden Matsch.
   */
  private pushFloat(
    unitId: number, amount: number, suffix: string, color: string, size: number,
    nowMs: number, ttlMs: number, mergeKey: string,
  ): void {
    if (mergeKey) {
      const existing = this.floats.find(
        (f) => f.unitId === unitId && f.mergeKey === mergeKey && f.bornMs === nowMs,
      )
      if (existing) {
        existing.amount += amount
        existing.suffix = suffix || existing.suffix
        existing.size = Math.max(existing.size, size)
        existing.text = `${existing.amount}${existing.suffix}`
        return
      }
    }
    const activeSlots = this.floats
      .filter((f) => f.unitId === unitId && nowMs - f.bornMs < 260)
      .map((f) => f.slot)
    let slot = 0
    while (activeSlots.includes(slot)) slot++
    this.floats.push({
      unitId, text: `${amount}${suffix}`, color, size, bornMs: nowMs, ttlMs,
      offsetX: (Math.random() - 0.5) * this.hexSize * 0.35, slot, mergeKey, amount, suffix,
    })
  }

  private pushLabel(unitId: number, text: string, color: string, size: number, nowMs: number, ttlMs: number): void {
    const activeSlots = this.floats.filter((f) => f.unitId === unitId && nowMs - f.bornMs < 260).map((f) => f.slot)
    let slot = 0
    while (activeSlots.includes(slot)) slot++
    this.floats.push({ unitId, text, color, size, bornMs: nowMs, ttlMs, offsetX: 0, slot, mergeKey: '', amount: 0, suffix: '' })
  }

  /** FX/Anim aus Events — Farben nach Keyword-Regel (docs/12 §2) */
  private spawnFx(
    e: SimEvent, nowMs: number,
    posFn: (id: number) => { x: number; y: number },
    snapFn: (id: number) => UnitSnapshot | undefined,
  ): void {
    const rAt = (pt: { x: number; y: number }): number => this.hexSize * 0.58 * this.scaleAtY(pt.y)
    switch (e.type) {
      case 'damage': {
        const isCrit = e.crit
        const pos = posFn(e.unit)
        if (e.dot) {
          // Partikel-Art nach vorhandenen Stacks (Gift-Blasen vs Brand-Glut)
          const snap = snapFn(e.unit)
          if (snap && snap.burnStacks > 0 && snap.poisonStacks === 0) {
            this.particles.embers(pos.x, pos.y, KEYWORD_COLORS.burn, nowMs, 4)
          } else {
            this.particles.bubbles(pos.x, pos.y, KEYWORD_COLORS.poison, nowMs, 3)
          }
        } else {
          this.anim.triggerHit(e.unit, nowMs)
          const sparkColor = isCrit ? KEYWORD_COLORS.crit : e.dtype === 'magic' ? KEYWORD_COLORS.shadow : '#ffe9c4'
          this.particles.sparks(pos.x, pos.y - rAt(pos) * 0.2, sparkColor, nowMs, isCrit ? 10 : 5, isCrit ? 130 : 85)
        }
        const color = e.dot
          ? KEYWORD_COLORS.poison
          : isCrit ? KEYWORD_COLORS.crit : e.dtype === 'magic' ? KEYWORD_COLORS.shadow : KEYWORD_COLORS.physical
        // DoT-Ticks mergen separat von direktem Schaden; Crits nicht mergen (sollen einzeln knallen)
        const mergeKey = e.dot ? 'dot' : isCrit ? '' : 'dmg'
        this.pushFloat(e.unit, e.amount, isCrit ? '!' : '', color, e.dot ? 13 : isCrit ? 24 : 16, nowMs, e.dot ? 650 : 900, mergeKey)
        break
      }
      case 'heal': {
        const pos = posFn(e.unit)
        this.particles.sparkles(pos.x, pos.y, KEYWORD_COLORS.holy, nowMs, 6)
        this.pushLabel(e.unit, `＋${e.amount}`, KEYWORD_COLORS.holy, 16, nowMs, 900)
        break
      }
      case 'shield': {
        const pos = posFn(e.unit)
        this.particles.shimmer(pos.x, pos.y, rAt(pos) * 1.05, KEYWORD_COLORS.shield, nowMs)
        this.pushLabel(e.unit, `+${e.amount} 🛡`, KEYWORD_COLORS.shield, 14, nowMs, 900)
        break
      }
      case 'attack': {
        const meta = this.metaById.get(e.unit)
        const from = posFn(e.unit)
        const to = posFn(e.target)
        this.anim.triggerAttack(e.unit, nowMs, to.x - from.x, to.y - from.y)
        if (meta && RANGED_WEAPONS.has(e.weapon)) {
          const rf = rAt(from)
          this.particles.projectile(from.x, from.y - rf * 0.25, to.x, to.y - rAt(to) * 0.2, TEAM_COLORS[meta.team], nowMs)
          this.particles.sparks(from.x + Math.sign(to.x - from.x) * rf * 0.6, from.y - rf * 0.25, '#ffd27a', nowMs, 3, 60)
        }
        break
      }
      case 'ability': {
        const names: Record<string, string> = {
          bulwark: 'Bollwerk', shadowstrike: 'Schattenstoß', piercingShot: 'Durchschlag',
          healingWave: 'Heilwelle', plagueCloud: 'Seuchenwolke',
        }
        const pos = posFn(e.unit)
        this.anim.triggerCast(e.unit, nowMs)
        abilityCastVfx(e.name, pos.x, pos.y, this.hexSize * 0.7 * this.scaleAtY(pos.y), nowMs, this.particles)
        this.pulses.push({
          unitId: e.unit, bornMs: nowMs, ttlMs: 600,
          color: ABILITY_COLORS[e.name] ?? '#ffffff',
          label: names[e.name] ?? e.name,
        })
        break
      }
      case 'jump': {
        const from = this.hexCenter(e.from.col, e.from.row)
        const to = this.hexCenter(e.to.col, e.to.row)
        this.particles.poof(from.x, from.y, nowMs, 5)
        this.particles.ring(to.x, to.y, KEYWORD_COLORS.shadow, nowMs, this.hexSize * 0.6 * this.scaleAtY(to.y))
        this.pulses.push({ unitId: e.unit, bornMs: nowMs, ttlMs: 400, color: KEYWORD_COLORS.shadow, label: 'Sprung!' })
        break
      }
      case 'death': {
        const pos = posFn(e.unit)
        this.anim.triggerDeath(e.unit, nowMs)
        this.particles.poof(pos.x, pos.y, nowMs, 8)
        break
      }
      case 'taunt':
        this.pushLabel(e.unit, '⚑', '#ffb347', 15, nowMs, 700)
        break
      default:
        break
    }
  }

  /** emotion aus Snapshot ableiten (docs/11 Expressions) */
  private emotionOf(s: UnitSnapshot, maxHp: number): Emotion {
    if (s.state === 'dead') return 'dead'
    if (s.state === 'ability') return 'surprised'
    if (s.hp * 100 < maxHp * 30) return 'worried'
    if (s.state === 'attacking') return 'angry'
    return 'neutral'
  }

  /** Draw battle frame at fractional simTime (ticks). nowMs: FX clock. */
  drawBattle(simTime: number, nowMs: number): void {
    const result = this.result
    if (!result) return
    const dtMs = this.lastFrameMs > 0 ? nowMs - this.lastFrameMs : 16
    this.lastFrameMs = nowMs
    const maxTick = result.snapshots.length - 1
    const t = Math.max(0, Math.min(maxTick, Math.floor(simTime)))
    const alpha = Math.max(0, Math.min(1, simTime - t))
    const snap = result.snapshots[t]!
    const snapNext = result.snapshots[Math.min(maxTick, t + 1)]
    const byId = new Map(snap.map((s) => [s.id, s]))
    const byIdNext = new Map(snapNext ? snapNext.map((s) => [s.id, s]) : [])

    const posCache = new Map<number, { x: number; y: number }>()
    for (const s of snap) {
      posCache.set(s.id, this.unitPos(s, byIdNext.get(s.id), alpha))
    }
    const posFn = (id: number): { x: number; y: number } => posCache.get(id) ?? { x: -100, y: -100 }
    const snapFn = (id: number): UnitSnapshot | undefined => byId.get(id)

    this.processEvents(simTime, nowMs, posFn, snapFn)

    this.beginFrame()
    this.blitGrid()
    const ctx = this.ctx

    // units — dead first (corpses under living), dann fern -> nah (Y-Sorting, 2.5D)
    const rBase = this.hexSize * 0.58
    const drawOrder = [...snap].sort(
      (a, b) => (a.state === 'dead' ? 0 : 1) - (b.state === 'dead' ? 0 : 1) || posFn(a.id).y - posFn(b.id).y,
    )
    for (const s of drawOrder) {
      const meta = this.metaById.get(s.id)
      if (!meta) continue
      const pos = posFn(s.id)
      const r = rBase * this.scaleAtY(pos.y) // Tiefen-Skala: nah gross, fern klein
      if (s.state === 'dead') {
        const dt = this.deathTick.get(s.id) ?? t
        // BATTLE-24: erst die Death-Anim ausspielen (20 Ticks), dann ausfaden
        const deathProgress = Math.min(1, (simTime - dt) / 20)
        const fade = Math.max(0, 1 - Math.max(0, simTime - dt - 20) / 30)
        if (fade <= 0) continue
        this.drawVeggie(pos.x, pos.y, r * 0.9, {
          champ: meta.champ, team: meta.team, tier: meta.tier ?? 1, weapons: [],
          emotion: 'dead', alpha: deathProgress < 1 ? 0.9 : fade * 0.45,
          pose: this.anim.getPose(s.id, nowMs, r, false),
          anim: { kind: 'death', timeMs: nowMs, progress: deathProgress },
        })
        continue
      }
      const pose = this.anim.getPose(s.id, nowMs, r, s.state === 'moving')
      // BATTLE-24: Sprite-Anim aus Sim-State; Attack-Frame haengt am AnimDirector-Schwung
      const spriteKind = s.state === 'moving' ? 'walk' as const
        : (s.state === 'attacking' || s.state === 'ability') && pose.attackSwing > 0 ? 'attack' as const
        : 'idle' as const
      this.drawVeggie(pos.x, pos.y, r, {
        champ: meta.champ, team: meta.team, tier: meta.tier ?? 1,
        weapons: meta.weapons, emotion: this.emotionOf(s, meta.maxHp), pose,
        anim: {
          kind: spriteKind,
          timeMs: nowMs,
          progress: spriteKind === 'attack' ? pose.attackSwing : undefined,
        },
      })

      // Sprites sind ~2.5r hoch (Painter ~1.8r) — Balken/Marker ueber den Kopf heben (BATTLE-24)
      const headY = spriteReady(meta.champ) ? r * 2.75 : r * 1.48

      // taunt marker
      if (s.taunted) {
        ctx.fillStyle = '#ffb347'
        ctx.font = `bold ${r * 0.55}px system-ui`
        ctx.textAlign = 'center'
        ctx.fillText('⚑', pos.x + r * 0.95, pos.y - headY + r * 0.5)
      }

      // bars
      const bw = r * 2.1
      const bx = pos.x - bw / 2
      let by = pos.y - headY
      const hpFrac = Math.max(0, Math.min(1, s.hp / meta.maxHp))
      ctx.fillStyle = 'rgba(10,8,5,0.65)'
      ctx.fillRect(bx - 1, by - 1, bw + 2, 7)
      const hpGrad = ctx.createLinearGradient(bx, by, bx, by + 5)
      const hpColor = meta.team === 'A' ? '#4dd06a' : '#e8564d'
      hpGrad.addColorStop(0, hpColor)
      hpGrad.addColorStop(1, darken(hpColor, 22))
      ctx.fillStyle = hpGrad
      ctx.fillRect(bx, by, bw * hpFrac, 5)
      if (s.shield > 0) {
        ctx.fillStyle = KEYWORD_COLORS.shield
        ctx.fillRect(bx, by - 3, bw * Math.min(1, s.shield / meta.maxHp), 2.5)
      }
      by += 7
      ctx.fillStyle = 'rgba(10,8,5,0.65)'
      ctx.fillRect(bx - 1, by - 1, bw + 2, 4)
      ctx.fillStyle = '#e8b33d'
      ctx.fillRect(bx, by, bw * Math.max(0, Math.min(1, s.mana / Math.max(1, meta.manaMax))), 2)
      // Sternstufe rechts neben den Balken (PO-Wunsch: nicht mehr auf der Figur)
      this.drawTierBadge(bx + bw + 4, pos.y - headY + 4, meta.tier ?? 1, r)

      // DoT stack badges (Keyword-Farben)
      let badgeX = pos.x - r
      const badgeY = pos.y + r * 1.18
      if (s.poisonStacks > 0) {
        this.drawBadge(badgeX, badgeY, KEYWORD_COLORS.poison, `${s.poisonStacks}`)
        badgeX += 17
      }
      if (s.burnStacks > 0) {
        this.drawBadge(badgeX, badgeY, KEYWORD_COLORS.burn, `${s.burnStacks}`)
      }
    }

    // Partikel + Projektile (ueber den Units)
    this.particles.updateAndDraw(ctx, nowMs, dtMs)

    // pulses (ability casts, Elementfarbe)
    for (const p of this.pulses) {
      const age = (nowMs - p.bornMs) / p.ttlMs
      if (age > 1) continue
      const pos = posFn(p.unitId)
      const rp = rBase * this.scaleAtY(pos.y)
      ctx.globalAlpha = 1 - age
      ctx.strokeStyle = p.color
      ctx.lineWidth = 3
      ctx.beginPath()
      ctx.ellipse(pos.x, pos.y + rp * 0.5, rp * (1 + age * 1.6), rp * (1 + age * 1.6) * TILT, 0, 0, Math.PI * 2)
      ctx.stroke()
      ctx.font = 'bold 13px system-ui'
      ctx.textAlign = 'center'
      ctx.lineWidth = 3
      ctx.strokeStyle = 'rgba(0,0,0,0.7)'
      ctx.strokeText(p.label, pos.x, pos.y - rp - 30)
      ctx.fillStyle = p.color
      ctx.fillText(p.label, pos.x, pos.y - rp - 30)
      ctx.globalAlpha = 1
    }
    this.pulses = this.pulses.filter((p) => nowMs - p.bornMs <= p.ttlMs)

    // floating texts — fette Trefferzahlen, gestapelt + Outline (F1, docs/11), oberster Layer
    for (const f of this.floats) {
      const age = (nowMs - f.bornMs) / f.ttlMs
      if (age > 1) continue
      const pos = posFn(f.unitId)
      const rf = rBase * this.scaleAtY(pos.y)
      ctx.globalAlpha = 1 - age * age
      ctx.font = `900 ${f.size}px system-ui`
      ctx.textAlign = 'center'
      // Slot staffelt vertikal (deutlich ueber der HP-Bar startend), Rise ueber Lebenszeit
      const fy = pos.y - rf * 1.7 - f.slot * (f.size + 3) - age * 26
      ctx.lineWidth = 4
      ctx.strokeStyle = 'rgba(10,8,5,0.85)'
      ctx.strokeText(f.text, pos.x + f.offsetX, fy)
      ctx.fillStyle = f.color
      ctx.fillText(f.text, pos.x + f.offsetX, fy)
      ctx.globalAlpha = 1
    }
    this.floats = this.floats.filter((f) => nowMs - f.bornMs <= f.ttlMs)
    this.endFrame()
  }

  private drawBadge(x: number, y: number, color: string, text: string): void {
    const ctx = this.ctx
    ctx.beginPath()
    ctx.arc(x, y, 7.5, 0, Math.PI * 2)
    ctx.fillStyle = color
    ctx.fill()
    ctx.strokeStyle = 'rgba(10,8,5,0.7)'
    ctx.lineWidth = 1.5
    ctx.stroke()
    ctx.fillStyle = '#171310'
    ctx.font = 'bold 9px system-ui'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, x, y + 0.5)
  }
}
